import type {
  ClassScheduleRow,
  ClassScheduleRowTimes,
  ClassScheduleSettings,
  SectionTimeMode,
  WeekdayKey,
} from '../services/sectionLayoutService';

export const WEEKDAY_TIME_KEYS: WeekdayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri'];

export function isWeekdayTimeKey(key: string | null | undefined): key is WeekdayKey {
  return key === 'mon' || key === 'tue' || key === 'wed' || key === 'thu' || key === 'fri';
}

export function getSectionTimeMode(settings: ClassScheduleSettings, sectionId: string): SectionTimeMode {
  return settings.sectionTimeMode?.[sectionId] ?? 'common';
}

export function extractRowTimes(row: ClassScheduleRow): ClassScheduleRowTimes {
  return {
    startTime: row.startTime ?? '',
    durationMinutes: row.durationMinutes ?? '',
    endTime: row.endTime ?? '',
  };
}

const HHMM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** 공통설정에 요일별 시드에 쓸 시작시각이 하나라도 있는지 */
export function hasCommonScheduleStartTimes(
  rows: Array<Pick<ClassScheduleRow, 'startTime'> | ClassScheduleRowTimes>
): boolean {
  return rows.some((row) => HHMM_RE.test((row.startTime ?? '').trim()));
}

export function mergeRowWithTimes(row: ClassScheduleRow, times: ClassScheduleRowTimes): ClassScheduleRow {
  return {
    ...row,
    startTime: times.startTime ?? '',
    durationMinutes: times.durationMinutes ?? '',
    endTime: times.endTime ?? '',
  };
}

/** 요일별 설정이면 해당 요일 시각을, 아니면 공통 sectionRows 시각을 반환한다. */
export function resolveClassScheduleRowsForWeekday(
  settings: ClassScheduleSettings,
  sectionId: string,
  weekdayKey: WeekdayKey | null | undefined
): ClassScheduleRow[] {
  const baseRows = settings.sectionRows[sectionId] ?? [];
  if (!weekdayKey || getSectionTimeMode(settings, sectionId) !== 'weekday') {
    return baseRows;
  }
  const dayTimes = settings.sectionWeekdayRowTimes?.[sectionId]?.[weekdayKey];
  if (!dayTimes?.length) return baseRows;
  return baseRows.map((row, idx) => mergeRowWithTimes(row, dayTimes[idx] ?? extractRowTimes(row)));
}

export function seedWeekdayRowTimesFromRows(rows: ClassScheduleRow[]): ClassScheduleRowTimes[] {
  const normalized = rows.slice(0, 10);
  while (normalized.length < 10) {
    normalized.push({
      kind: 'class',
      name: '',
      startTime: '',
      durationMinutes: '',
      endTime: '',
    });
  }
  return normalized.map(extractRowTimes);
}

export function seedAllWeekdayRowTimes(rows: ClassScheduleRow[]): Record<WeekdayKey, ClassScheduleRowTimes[]> {
  const seed = seedWeekdayRowTimesFromRows(rows);
  return {
    mon: seed.map((t) => ({ ...t })),
    tue: seed.map((t) => ({ ...t })),
    wed: seed.map((t) => ({ ...t })),
    thu: seed.map((t) => ({ ...t })),
    fri: seed.map((t) => ({ ...t })),
  };
}

export function applyTimesToRows(baseRows: ClassScheduleRow[], times: ClassScheduleRowTimes[]): ClassScheduleRow[] {
  return baseRows.map((row, idx) => mergeRowWithTimes(row, times[idx] ?? extractRowTimes(row)));
}
